// Sugalarm — the billing seam (cycle 464).
//
// The ONE place a billing surface (the onboarding "Start your free month"
// step, Settings → Account → Subscription, the kiosk status card) learns
// which payment rails it may show and how to start, restore or manage a
// subscription. Platform-gated like the cycle-316 `alarmReliability` seam:
// native calls go through the bridge-injected `window.Capacitor.Plugins`
// proxy (never a bare `import("@capacitor/...")`), and every call no-ops
// safely where the plugin is absent.
//
// ⚠ THE KIOSK CAN NEVER BE USED TO SUBSCRIBE. `getBillingSupport()` checks
// the kiosk FIRST and answers `supported:false, platform:"kiosk"` even if a
// native bridge claims billing support; `allowedRails` gives the kiosk no
// rail at all; every action below refuses on the kiosk before it touches a
// plugin or a URL.
//
// ⚠ NO ENTITLEMENT DECISION LIVES HERE. A store purchase that resolves is
// only a claim: the UI shows "Confirming with the store..." and polls
// `GET /api/me/subscription` until the SERVER's state changes. Nothing in
// this module ever reports "subscribed".
//
// Cycle 465 (StoreKit 2): the iOS archive registers the native `Billing`
// plugin, and this seam is its only caller. The product to show and the
// account's OPAQUE purchase token come from `GET /api/billing/apple/offer`
// (the caller's `accountToken`, the account id, is never handed to the
// store); every transaction the plugin hands back is a signed JWS posted to
// `POST /api/billing/apple/verify`, and the transaction is finished on the
// device ONLY after the server accepted it. An older iOS archive (no
// plugin) shows nothing new.
//
// Cycle 466 (Google Play Billing): the `standard` Android APK registers its
// own native `Billing` plugin (the same name on the bridge; the kiosk APK
// never compiles it). The product, base plan, offer and the account's
// OPAQUE purchase token come from `GET /api/billing/google/offer`; the plugin
// sets that token as the purchase's obfuscated account id; every purchase
// token the plugin hands back (a purchase, a restore, a `purchaseUpdated`
// event) is posted to `POST /api/billing/google/verify`, where the server
// re-reads it from the Android Publisher API, binds it to the account and
// ACKNOWLEDGES it. Nothing is acknowledged or finished on the device. An
// older Android APK (no plugin) still answers `unsupported`.

import {
  getAppleOffer,
  getGoogleOffer,
  verifyAppleTransaction,
  verifyGooglePurchase,
  type Subscription,
} from "../api/subscription";
import { isAuthedAsDevice, isKiosk } from "./auth";
import { capacitorPlatform, isCapacitorNative, isNativeKioskShell } from "./capacitor";

export type BillingPlatform = "ios" | "android" | "web" | "kiosk";

export interface BillingSupport {
  supported: boolean;
  platform: BillingPlatform;
}

export interface AllowedRails {
  /** The in-app store purchase rail, or null where none may render. */
  store: "apple" | "google" | null;
  /** The "Pay on thesugaralarm.com" rail (Stripe: card plus the device's own
   *  wallet). On web it IS the purchase path. */
  webLink: boolean;
}

/** The fixed subscribe page. The web purchase path opens exactly this URL;
 *  nothing is built from server or user input. */
export const SUBSCRIBE_URL = "https://thesugaralarm.com/subscribe.html";

/** The only URL prefixes the seam will ever open. A server-minted handoff
 *  (`POST /api/billing/web/handoff`) or portal URL that does not start with
 *  one of these is refused, never opened. */
const HANDOFF_URL_PREFIX = `${SUBSCRIBE_URL}#h=`;
const STRIPE_PORTAL_PREFIX = "https://billing.stripe.com/";
export const APPLE_MANAGE_URL = "https://apps.apple.com/account/subscriptions";
export const GOOGLE_MANAGE_URL = "https://play.google.com/store/account/subscriptions";
/** The store-required Terms and Privacy links under the purchase button. */
export const TERMS_URL = "https://thesugaralarm.com/terms-of-sale";
export const PRIVACY_URL = "https://thesugaralarm.com/privacy-policy";

/** Google Play's US external content links program. The operator has not
 *  enrolled; while false the Android app never shows the website link. */
export const ANDROID_EXTERNAL_LINKS_ENROLLED = false;

/** The one rule for which rails a surface may render (the payment rail
 *  matrix, operator 2026-10-09). `storefront` is the store's country code
 *  (`getStorefront()`, arriving in 465/466); null until then, so `webLink`
 *  is false on native this cycle.
 *
 *  iPhone: Apple IAP, plus the website link in the US storefront only.
 *  Android: Google Play, plus the website link only in the US Play country
 *  AND while enrolled in the external content links program.
 *  Web: no store rail; the website (Stripe Checkout) is the path.
 *  Kiosk: nothing. The iOS shell never gets a Google rail and the Android
 *  shell never an Apple rail. */
export function allowedRails(
  platform: BillingPlatform,
  storefront: string | null,
): AllowedRails {
  const country = (storefront ?? "").trim().toUpperCase();
  switch (platform) {
    case "ios":
      // StoreKit reports ISO 3166-1 alpha-3 ("USA"); accept alpha-2 too.
      return { store: "apple", webLink: country === "USA" || country === "US" };
    case "android":
      return {
        store: "google",
        webLink:
          ANDROID_EXTERNAL_LINKS_ENROLLED && (country === "US" || country === "USA"),
      };
    case "web":
      return { store: null, webLink: true };
    case "kiosk":
    default:
      return { store: null, webLink: false };
  }
}

// ─── Native plugin (cycles 465/466) ───────────────────────────────────

export interface NativeOffer {
  productId: string;
  priceDisplay: string;
  hasTrial: boolean;
  /** Cycle 465: the server-issued purchase token that came with this
   *  offer, so a purchase of exactly the offer on screen needs no second
   *  round trip. Never the account id. On Android (466) it is the same
   *  value, set as the purchase's obfuscated account id. */
  appAccountToken?: string;
  /** Cycle 466, Android only: the Play base plan and the free-month offer
   *  (null = no offer: the account used its trial, or the store does not
   *  carry the offer) the purchase on screen will ask the store for. */
  basePlanId?: string;
  offerId?: string | null;
}

/** Why a purchase or restore did not complete. `cancelled` and `pending`
 *  (Ask to Buy) are decisions, not errors: no error is shown for them. */
export type BillingFailureReason =
  | "unsupported"
  | "cancelled"
  | "pending"
  | "failed"
  | "kiosk"
  | "refused_url"
  | "unavailable"
  | "mismatch"
  | "none";

/** `subscription` (cycle 465) is the SERVER's own answer from the verify
 *  that accepted the transaction: the same body `GET /api/me/subscription`
 *  returns, so a caller may take a granting answer as confirmed without
 *  polling (an already-recorded subscription restored on a reinstall never
 *  changes the polled fingerprint). Never a client-made value. */
export type BillingActionResult =
  | { ok: true; subscription?: Subscription }
  | { ok: false; reason: BillingFailureReason };

/** The states in which the server says the account has service. */
export function subscriptionGrants(s: Pick<Subscription, "state"> | null | undefined): boolean {
  return s?.state === "trial" || s?.state === "active" || s?.state === "comped";
}

/** Cycle 465: a verified StoreKit transaction as the iOS plugin hands it
 *  over. The JWS is what the server verifies; the ids are only for
 *  `finish` and for logging on the device, never sent anywhere else. */
export interface NativeTransaction {
  jws: string;
  transactionId: string;
  originalTransactionId?: string;
  productId?: string;
}

export interface NativeProduct {
  id: string;
  displayPrice: string;
  displayName?: string;
  hasIntroOffer?: boolean;
}

type NativePurchaseResult =
  | {
      status: "success";
      jws?: string;
      transactionId?: string;
      originalTransactionId?: string;
      productId?: string;
    }
  | { status: "cancelled" | "pending" | "unverified" | "failed"; reason?: string };

type NativeRestoreResult =
  | { status: "success"; transactions?: NativeTransaction[]; purchases?: NativeGooglePurchase[] }
  | { status: "none" | "failed" | "unavailable"; reason?: string };

/** Cycle 466: a Play purchase as the Android plugin hands it over. The
 *  purchase token is what the server re-reads from the Android Publisher
 *  API; it is posted to `verify` and nowhere else. */
export interface NativeGooglePurchase {
  purchaseToken: string;
  orderId?: string | null;
  productIds?: string[];
  /** Play's purchase state: a `pending` purchase (a deferred payment) has
   *  no entitlement yet; the plugin forwards it later as `purchaseUpdated`. */
  purchaseState?: "purchased" | "pending" | "unspecified";
  acknowledged?: boolean;
}

/** Cycle 466: one Play subscription offer (a base plan, or a base plan plus
 *  an offer) with its pricing phases, as the plugin reads it from
 *  `queryProductDetailsAsync`. */
export interface NativeGoogleOffer {
  basePlanId: string;
  offerId?: string | null;
  offerToken?: string;
  pricingPhases?: Array<{
    formattedPrice: string;
    priceAmountMicros?: number;
    billingPeriod?: string;
    recurrenceMode?: number;
  }>;
}

type NativeGooglePurchaseResult =
  | { status: "success"; purchase?: NativeGooglePurchase }
  | { status: "cancelled" | "pending" | "already_owned" | "failed" | "unavailable"; reason?: string };

interface ListenerHandle {
  remove: () => Promise<void> | void;
}

/** The native `Billing` plugin's contract (`mobile/ios/App/App/Billing.swift`
 *  and, cycle 466, `mobile/android/app/src/standard/.../BillingPlugin.java`,
 *  the `standard` flavor only). Every method is optional here so an older
 *  archive or APK, or a partial plugin, degrades to "unsupported". */
interface BillingPluginProxy {
  getProducts?(opts: {
    ids: string[];
  }): Promise<{ products?: Array<NativeProduct & { offers?: NativeGoogleOffer[] }> }>;
  purchase?(
    opts:
      | { productId: string; appAccountToken: string }
      | { productId: string; basePlanId: string; offerId?: string; obfuscatedAccountId: string },
  ): Promise<NativePurchaseResult | NativeGooglePurchaseResult>;
  restore?(): Promise<NativeRestoreResult>;
  finish?(opts: { transactionId: string }): Promise<{ finished: boolean }>;
  manageSubscriptions?(opts?: { productId?: string }): Promise<void>;
  getStorefront?(): Promise<{ countryCode: string | null }>;
  addListener?(
    event: "transactionUpdated" | "purchaseUpdated",
    cb: (ev: NativeTransaction & NativeGooglePurchase) => void,
  ): Promise<ListenerHandle> | ListenerHandle;
}

/** True on every kiosk surface: the official Android kiosk APK, the Pi /
 *  browser kiosk flag, or any device-token session (a paired kiosk). */
export function isKioskSurface(): boolean {
  try {
    return isNativeKioskShell() || isKiosk() || isAuthedAsDevice();
  } catch {
    // When in doubt, it is a kiosk: the failure mode is "no billing UI".
    return true;
  }
}

function plugin(): BillingPluginProxy | null {
  if (isKioskSurface()) return null;
  if (!isCapacitorNative()) return null;
  const cap = (window as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  const p = cap?.Plugins?.Billing;
  return p != null ? (p as BillingPluginProxy) : null;
}

/** Which billing surface this is, and whether it can sell. The kiosk is
 *  checked FIRST, so a native bridge that claims support cannot reach it. */
export function getBillingSupport(): BillingSupport {
  if (isKioskSurface()) return { supported: false, platform: "kiosk" };
  if (isCapacitorNative()) {
    const platform = capacitorPlatform();
    if (platform === "ios" || platform === "android") {
      const p = plugin();
      return { supported: p != null && typeof p.purchase === "function", platform };
    }
  }
  // Plain browser (with the cycle-281 override) and app.sugalarm.com: the
  // website checkout is always available.
  return { supported: true, platform: "web" };
}

/** The native store's storefront country, or null (no plugin yet, or the
 *  web / kiosk). */
export async function getStorefront(): Promise<string | null> {
  const p = plugin();
  if (p == null || typeof p.getStorefront !== "function") return null;
  try {
    return (await p.getStorefront()).countryCode ?? null;
  } catch {
    return null;
  }
}

/** The offer to show. The web uses the server's static descriptor; native
 *  asks the store (null when no plugin). */
export async function getOffer(opts: {
  trialEligible: boolean;
  priceDisplay: string;
}): Promise<NativeOffer | null> {
  const support = getBillingSupport();
  if (support.platform === "kiosk") return null;
  if (support.platform === "web") {
    return {
      productId: "sugaralarm_monthly",
      priceDisplay: opts.priceDisplay,
      hasTrial: opts.trialEligible,
    };
  }
  const p = plugin();
  if (p == null) return null;
  if (support.platform === "ios") return appleOffer(p, opts.priceDisplay);
  return googleOffer(p, opts.priceDisplay);
}

/** iOS: the SERVER picks the product (the trial product only while the
 *  account never used a trial on any rail); the store supplies the
 *  localized price, with the server's static price standing in when the
 *  store does not answer. Null when the server cannot offer (not configured,
 *  signed out, offline): the caller shows that purchases are unavailable. */
async function appleOffer(p: BillingPluginProxy, fallbackPrice: string): Promise<NativeOffer | null> {
  let offer: Awaited<ReturnType<typeof getAppleOffer>>;
  try {
    offer = await getAppleOffer();
  } catch {
    return null;
  }
  if (!offer.ok) return null;
  let priceDisplay = fallbackPrice;
  // Apple decides intro-offer eligibility per Apple ID: the server's
  // "this account never used a trial" is intersected with the store's
  // answer, so an Apple ID that already used its free month is shown
  // "Subscribe" at the full price, never a free month the sheet will not
  // give (astra, 465). No answer from the store keeps the server's word.
  let hasTrial = offer.data.has_trial;
  if (typeof p.getProducts === "function") {
    try {
      const got = await p.getProducts({ ids: [offer.data.product_id] });
      const product = got?.products?.find((x) => x.id === offer.data.product_id);
      if (product?.displayPrice) priceDisplay = `${product.displayPrice}/month`;
      if (product && product.hasIntroOffer === false) hasTrial = false;
    } catch {
      /* the server's static price and word stand in */
    }
  }
  return {
    productId: offer.data.product_id,
    priceDisplay,
    hasTrial,
    appAccountToken: offer.data.app_account_token,
  };
}

/** One transaction, settled: verified by the SERVER (bound to this account
 *  through the opaque purchase token, re-checked with the App Store Server
 *  API) and only then finished on the device. A verify the server refused
 *  leaves the transaction unfinished: StoreKit re-delivers it on the next
 *  launch and the server answers the same way. */
async function settleAppleTransaction(
  p: BillingPluginProxy,
  t: NativeTransaction,
): Promise<BillingActionResult> {
  let verified: Awaited<ReturnType<typeof verifyAppleTransaction>>;
  try {
    verified = await verifyAppleTransaction(t.jws);
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (!verified.ok) {
    if (verified.error.status === 403) return { ok: false, reason: "mismatch" };
    if (verified.error.status === 503) return { ok: false, reason: "unavailable" };
    return { ok: false, reason: "failed" };
  }
  if (typeof p.finish === "function") {
    try {
      await p.finish({ transactionId: t.transactionId });
    } catch {
      /* unfinished: re-delivered next launch; the server already holds it */
    }
  }
  return { ok: true, subscription: verified.data };
}

async function applePurchase(
  p: BillingPluginProxy,
  opts: { productId: string; appAccountToken?: string },
): Promise<BillingActionResult> {
  // The product and the purchase token come from the SERVER: either the
  // offer the screen loaded (its product and server-issued token ride
  // along, so the store sells exactly what was shown) or a fresh offer. The
  // caller's `accountToken` (the account id) is never handed to the store.
  let productId = opts.productId;
  let appAccountToken = opts.appAccountToken;
  if (!appAccountToken) {
    let offer: Awaited<ReturnType<typeof getAppleOffer>>;
    try {
      offer = await getAppleOffer();
    } catch {
      return { ok: false, reason: "failed" };
    }
    if (!offer.ok) return { ok: false, reason: offer.error.status === 503 ? "unavailable" : "failed" };
    productId = offer.data.product_id;
    appAccountToken = offer.data.app_account_token;
  }
  let r: NativePurchaseResult;
  try {
    // The iOS plugin answers the StoreKit shape; the proxy type is the
    // union of both platforms' contracts.
    r = (await p.purchase!({ productId, appAccountToken })) as NativePurchaseResult;
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (r.status === "cancelled") return { ok: false, reason: "cancelled" };
  if (r.status === "pending") return { ok: false, reason: "pending" };
  if (r.status !== "success" || !r.jws || !r.transactionId) return { ok: false, reason: "failed" };
  return settleAppleTransaction(p, {
    jws: r.jws,
    transactionId: r.transactionId,
    originalTransactionId: r.originalTransactionId,
    productId: r.productId,
  });
}

async function appleRestore(p: BillingPluginProxy): Promise<BillingActionResult> {
  let r: NativeRestoreResult;
  try {
    r = await p.restore!();
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (r.status === "none") return { ok: false, reason: "none" };
  if (r.status !== "success" || !r.transactions?.length) return { ok: false, reason: "failed" };
  let accepted: Subscription | undefined;
  let settled = false;
  let last: BillingFailureReason = "failed";
  for (const t of r.transactions) {
    if (!t?.jws || !t.transactionId) continue;
    const s = await settleAppleTransaction(p, t);
    if (s.ok) {
      settled = true;
      accepted = s.subscription ?? accepted;
    } else {
      last = s.reason;
    }
  }
  return settled ? { ok: true, subscription: accepted } : { ok: false, reason: last };
}

// ─── Android: Google Play Billing (cycle 466) ─────────────────────────

/** The recurring price of a Play offer: the LAST pricing phase with a
 *  non-zero amount (a free-month offer lists its free phase first). */
function googleRecurringPrice(offer: NativeGoogleOffer | undefined): string | null {
  const phases = offer?.pricingPhases ?? [];
  for (let i = phases.length - 1; i >= 0; i -= 1) {
    const ph = phases[i];
    if (ph?.formattedPrice && (ph.priceAmountMicros == null || ph.priceAmountMicros > 0)) {
      return ph.formattedPrice;
    }
  }
  return null;
}

/** Android: the SERVER picks the product, the base plan and whether the
 *  free-month offer is shown (the one-trial-per-account rule; the offer is
 *  developer-determined on Play's side, so the server's word is the whole
 *  rule); the store supplies the localized price, with the server's static
 *  price standing in when the store does not answer. A store that does not
 *  carry the offer the server named drops it (the sheet would refuse it).
 *  Null when the server cannot offer (not configured, signed out, offline):
 *  the caller shows that purchases are unavailable. */
async function googleOffer(p: BillingPluginProxy, fallbackPrice: string): Promise<NativeOffer | null> {
  let offer: Awaited<ReturnType<typeof getGoogleOffer>>;
  try {
    offer = await getGoogleOffer();
  } catch {
    return null;
  }
  if (!offer || !offer.ok) return null;
  let priceDisplay = fallbackPrice;
  let offerId: string | null = offer.data.offer_id;
  if (typeof p.getProducts === "function") {
    try {
      const got = await p.getProducts({ ids: [offer.data.product_id] });
      const product = got?.products?.find((x) => x.id === offer.data.product_id);
      const offers = product?.offers ?? [];
      const base = offers.find((o) => o.basePlanId === offer.data.base_plan_id && !o.offerId);
      const trial = offerId
        ? offers.find((o) => o.basePlanId === offer.data.base_plan_id && o.offerId === offerId)
        : undefined;
      const price = googleRecurringPrice(trial) ?? googleRecurringPrice(base) ?? product?.displayPrice;
      if (price) priceDisplay = `${price}/month`;
      if (offerId && offers.length > 0 && trial == null) offerId = null;
    } catch {
      /* the server's static price and word stand in */
    }
  }
  return {
    productId: offer.data.product_id,
    priceDisplay,
    hasTrial: offerId != null,
    appAccountToken: offer.data.obfuscated_account_id,
    basePlanId: offer.data.base_plan_id,
    offerId,
  };
}

/** One Play purchase, settled: posted to the SERVER, which re-reads it from
 *  the Android Publisher API, binds it to this account through the
 *  obfuscated account id, writes it and ACKNOWLEDGES it. Nothing is
 *  acknowledged on the device: a verify the server refused leaves the
 *  purchase unacknowledged, Play re-delivers it as `purchaseUpdated` on the
 *  next launch and refunds it after three days if no server ever accepts it. */
async function settleGooglePurchase(
  t: NativeGooglePurchase,
  productId: string,
): Promise<BillingActionResult> {
  if (t.purchaseState === "pending") return { ok: false, reason: "pending" };
  let verified: Awaited<ReturnType<typeof verifyGooglePurchase>>;
  try {
    verified = await verifyGooglePurchase(t.purchaseToken, productId);
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (!verified.ok) {
    if (verified.error.status === 403) return { ok: false, reason: "mismatch" };
    if (verified.error.status === 503) return { ok: false, reason: "unavailable" };
    return { ok: false, reason: "failed" };
  }
  return { ok: true, subscription: verified.data };
}

function isGoogleResult(r: NativePurchaseResult | NativeGooglePurchaseResult): r is NativeGooglePurchaseResult {
  return r.status === "already_owned" || r.status === "unavailable" || (r.status === "success" && "purchase" in r);
}

async function googlePurchase(
  p: BillingPluginProxy,
  opts: { productId: string; basePlanId?: string; offerId?: string | null; appAccountToken?: string },
): Promise<BillingActionResult> {
  // The product, base plan, offer and the purchase token come from the
  // SERVER: either the offer the screen loaded (so the store sells exactly
  // what was shown) or a fresh offer. The caller's `accountToken` (the
  // account id) is never handed to the store.
  let productId = opts.productId;
  let basePlanId = opts.basePlanId;
  let offerId = opts.offerId;
  let obfuscatedAccountId = opts.appAccountToken;
  if (!obfuscatedAccountId || !basePlanId) {
    let offer: Awaited<ReturnType<typeof getGoogleOffer>>;
    try {
      offer = await getGoogleOffer();
    } catch {
      return { ok: false, reason: "failed" };
    }
    if (!offer) return { ok: false, reason: "failed" };
    if (!offer.ok) return { ok: false, reason: offer.error.status === 503 ? "unavailable" : "failed" };
    productId = offer.data.product_id;
    basePlanId = offer.data.base_plan_id;
    offerId = offer.data.offer_id;
    obfuscatedAccountId = offer.data.obfuscated_account_id;
  }
  let r: NativePurchaseResult | NativeGooglePurchaseResult;
  try {
    r = await p.purchase!({
      productId,
      basePlanId,
      ...(offerId ? { offerId } : {}),
      obfuscatedAccountId,
    });
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (r.status === "cancelled") return { ok: false, reason: "cancelled" };
  if (r.status === "pending") return { ok: false, reason: "pending" };
  // Play says this Google account already owns the subscription: the
  // purchase is a restore (Play Billing's ITEM_ALREADY_OWNED).
  if (r.status === "already_owned") return googleRestore(p);
  if (r.status === "unavailable") return { ok: false, reason: "unavailable" };
  if (r.status !== "success" || !isGoogleResult(r) || !r.purchase?.purchaseToken) {
    return { ok: false, reason: "failed" };
  }
  return settleGooglePurchase(r.purchase, productId);
}

async function googleRestore(p: BillingPluginProxy): Promise<BillingActionResult> {
  let r: NativeRestoreResult;
  try {
    r = await p.restore!();
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (r.status === "none") return { ok: false, reason: "none" };
  // A Play outage is not "nothing to restore" (regression-guard, 466).
  if (r.status === "unavailable") return { ok: false, reason: "unavailable" };
  if (r.status !== "success") return { ok: false, reason: "failed" };
  if (!r.purchases?.length) return { ok: false, reason: "none" };
  let accepted: Subscription | undefined;
  let settled = false;
  let last: BillingFailureReason = "failed";
  for (const t of r.purchases) {
    if (!t?.purchaseToken) continue;
    const productId = t.productIds?.[0];
    if (!productId) continue;
    const s = await settleGooglePurchase(t, productId);
    if (s.ok) {
      settled = true;
      accepted = s.subscription ?? accepted;
    } else {
      last = s.reason;
    }
  }
  return settled ? { ok: true, subscription: accepted } : { ok: false, reason: last };
}

/** Cycle 465: the iOS shell forwards every StoreKit transaction update
 *  while the app runs (a renewal, a refund, an Ask to Buy approval, a
 *  purchase finished on another device; events are retained until the SPA
 *  listens). Each is settled the same way as a purchase: verified by the
 *  server, finished only after. `onSettled` runs after each accepted one.
 *  Returns a detach function; a no-op on the web, the kiosk or without
 *  the plugin.
 *
 *  Cycle 466: the Android shell forwards every Play purchase update the
 *  same way (`purchaseUpdated`: a deferred payment completing, a purchase
 *  the app missed); each is posted to the server's verify. */
export function attachNativeTransactionListener(
  onSettled: (subscription?: Subscription) => void,
): () => void {
  const p = plugin();
  const platform = capacitorPlatform();
  if (p == null || typeof p.addListener !== "function" || (platform !== "ios" && platform !== "android")) {
    return () => {};
  }
  let handle: ListenerHandle | null = null;
  let detached = false;
  const event = platform === "ios" ? "transactionUpdated" : "purchaseUpdated";
  try {
    Promise.resolve(
      p.addListener(event, (ev) => {
        let settling: Promise<BillingActionResult>;
        if (platform === "ios") {
          if (!ev?.jws || !ev.transactionId) return;
          settling = settleAppleTransaction(p, ev);
        } else {
          const productId = ev?.productIds?.[0];
          if (!ev?.purchaseToken || !productId) return;
          settling = settleGooglePurchase(ev, productId);
        }
        void settling.then((r) => {
          // A settlement that lands after the detach (a sign-out, another
          // account's shell) reports nothing: its answer belongs to the
          // session that started it (astra round 2).
          if (r.ok && !detached) onSettled(r.subscription);
        });
      }),
    )
      .then((h) => {
        if (detached) void h?.remove?.();
        else handle = h;
      })
      .catch(() => {
        /* no listener: the next launch re-delivers the transaction */
      });
  } catch {
    return () => {};
  }
  return () => {
    detached = true;
    if (handle) void handle.remove();
  };
}

/** The customer-facing line for a purchase or restore that did not
 *  complete; null when nothing should be said (a cancellation, the kiosk). */
export function billingFailureMessage(
  reason: BillingFailureReason,
  action: "purchase" | "restore",
): string | null {
  switch (reason) {
    case "cancelled":
    case "kiosk":
      return null;
    case "pending":
      return "Waiting for approval. Once it is approved, your subscription will show here.";
    case "mismatch":
      return "That purchase belongs to a different account. Sign in with the account that made it.";
    case "unavailable":
      return capacitorPlatform() === "android"
        ? "Google Play purchases aren't available right now. Try again in a little while."
        : "App Store purchases aren't available right now. Try again in a little while.";
    case "unsupported":
      return action === "restore"
        ? "Restoring isn't available in this version of the app yet."
        : "Purchases aren't available in this version of the app yet.";
    case "none":
      return "Nothing to restore was found.";
    case "refused_url":
    case "failed":
    default:
      return action === "restore"
        ? "Nothing to restore was found."
        : "The store couldn't complete the purchase.";
  }
}

/** Web only: open a blank tab SYNCHRONOUSLY inside the tap, to be pointed at
 *  a URL that is still being fetched. A `window.open` after an `await` is
 *  outside the user gesture and a popup blocker (Safari) drops it silently;
 *  this one is not (security review LOW). Null in the native shells (the
 *  system browser takes the navigation) and on any kiosk. */
export function prepareExternalTab(): Window | null {
  if (isKioskSurface() || isCapacitorNative()) return null;
  try {
    const w = window.open("about:blank", "_blank");
    if (w) {
      try {
        w.opener = null;
      } catch {
        /* cross-origin already: nothing to sever */
      }
    }
    return w;
  } catch {
    return null;
  }
}

/** The longest a button waits for a server-minted billing URL before it
 *  gives up and closes its prepared tab (review round 2, L3). */
export const BILLING_URL_TIMEOUT_MS = 10_000;

/** Race a URL request against `BILLING_URL_TIMEOUT_MS`; a timeout reads as
 *  a failure (`null`), never as a URL. */
export async function withBillingTimeout<T>(p: Promise<T>): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), BILLING_URL_TIMEOUT_MS);
  });
  try {
    return await Promise.race([p, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Close a prepared tab that will not be used (the URL request failed). */
export function discardExternalTab(tab: Window | null): void {
  try {
    tab?.close();
  } catch {
    /* already gone */
  }
}

/** Open a URL outside the app. In the native shells a navigation to another
 *  host is handed to the system browser by Capacitor (⚠ only while neither
 *  thesugaralarm.com nor billing.stripe.com is in `server.allowNavigation`
 *  in mobile/capacitor.config.ts — adding either would load the page, and a
 *  handoff token, INSIDE the app's WebView); on the web it uses the tab
 *  prepared inside the tap, else a new tab. Only the fixed billing prefixes
 *  are ever accepted. */
export function openExternal(url: string, tab: Window | null = null): BillingActionResult {
  if (isKioskSurface()) {
    discardExternalTab(tab);
    return { ok: false, reason: "kiosk" };
  }
  if (!isAllowedBillingUrl(url)) {
    discardExternalTab(tab);
    return { ok: false, reason: "refused_url" };
  }
  try {
    if (isCapacitorNative()) {
      window.location.assign(url);
    } else if (tab && !tab.closed) {
      tab.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return { ok: true };
  } catch {
    discardExternalTab(tab);
    return { ok: false, reason: "failed" };
  }
}

export function isAllowedBillingUrl(url: string): boolean {
  if (typeof url !== "string") return false;
  return (
    url === SUBSCRIBE_URL ||
    url.startsWith(HANDOFF_URL_PREFIX) ||
    url.startsWith(STRIPE_PORTAL_PREFIX) ||
    url === APPLE_MANAGE_URL ||
    url === GOOGLE_MANAGE_URL ||
    url === TERMS_URL ||
    url === PRIVACY_URL
  );
}

/** Start a purchase. Web: opens the fixed subscribe page in a new tab.
 *  Native: the store sheet (465/466); `{ok:true}` means only "the store
 *  accepted it" and the caller MUST confirm with the server. */
export async function purchase(opts: {
  productId: string;
  accountToken: string;
  /** The server-issued purchase token that came with the offer on screen
   *  (`NativeOffer.appAccountToken`); without it the seam asks the server
   *  for a fresh offer. Never the account id. */
  appAccountToken?: string;
  /** Cycle 466, Android: the base plan and offer of the offer on screen
   *  (`NativeOffer.basePlanId` / `offerId`); without them the seam asks the
   *  server for a fresh offer. */
  basePlanId?: string;
  offerId?: string | null;
}): Promise<BillingActionResult> {
  const support = getBillingSupport();
  if (support.platform === "kiosk") return { ok: false, reason: "kiosk" };
  if (support.platform === "web") return openExternal(SUBSCRIBE_URL);
  const p = plugin();
  if (p == null || typeof p.purchase !== "function") return { ok: false, reason: "unsupported" };
  if (support.platform === "ios") {
    return applePurchase(p, { productId: opts.productId, appAccountToken: opts.appAccountToken });
  }
  // Android (cycle 466): routed through the server-issued purchase token
  // exactly as iOS; the caller's `accountToken` (the account id) is never
  // handed to the plugin (security review, 465).
  return googlePurchase(p, {
    productId: opts.productId,
    basePlanId: opts.basePlanId,
    offerId: opts.offerId,
    appAccountToken: opts.appAccountToken,
  });
}

/** Restore purchases (native only). Same confirmation rule as `purchase`. */
export async function restore(): Promise<BillingActionResult> {
  const support = getBillingSupport();
  if (support.platform === "kiosk") return { ok: false, reason: "kiosk" };
  const p = plugin();
  if (p == null || typeof p.restore !== "function") return { ok: false, reason: "unsupported" };
  if (support.platform === "ios") return appleRestore(p);
  return googleRestore(p);
}

// ─── Manage ───────────────────────────────────────────────────────────

/** What "Manage subscription" does for an account billed through `source`
 *  on this platform. `stripe_text` = the iPhone app tells a Stripe-billed
 *  account to manage on the website, with no button. */
export type ManageAction =
  | "apple_store"
  | "google_store"
  | "stripe_portal"
  | "stripe_text"
  | "google_text"
  | "apple_text"
  | null;

/** Cycle 465: a store's subscription is managed on ITS platform. The
 *  iPhone app never shows a Google Play control (and the Android app never
 *  an App Store one): a cross-store account gets a line of text, no
 *  button, the same way a Stripe-billed account does on the iPhone. */
export function manageActionFor(
  source: string | null,
  platform: BillingPlatform,
): ManageAction {
  if (platform === "kiosk") return null;
  switch (source) {
    case "apple":
      return platform === "android" ? "apple_text" : "apple_store";
    case "google":
      return platform === "ios" ? "google_text" : "google_store";
    case "stripe":
      return platform === "ios" ? "stripe_text" : "stripe_portal";
    default:
      return null;
  }
}

/** The one line shown for a text-only manage action (no em dashes). */
export function manageTextFor(action: ManageAction): string | null {
  switch (action) {
    case "stripe_text":
      return "Manage your subscription at thesugaralarm.com";
    case "google_text":
      return "Billed through Google Play. Manage it on your Android device.";
    case "apple_text":
      return "Billed through the App Store. Manage it on your iPhone.";
    default:
      return null;
  }
}

/** Open the store's own management page. On native the plugin's
 *  `manageSubscriptions` (465/466) is preferred; the fixed store URLs are the
 *  fallback. The Stripe portal is opened by the caller with the URL from
 *  `POST /api/billing/stripe/portal` (checked against the allowlist). */
export async function manageSubscriptions(
  action: "apple_store" | "google_store",
): Promise<BillingActionResult> {
  if (isKioskSurface()) return { ok: false, reason: "kiosk" };
  const p = plugin();
  if (p != null && typeof p.manageSubscriptions === "function") {
    const platform = capacitorPlatform();
    const own =
      (action === "apple_store" && platform === "ios") ||
      (action === "google_store" && platform === "android");
    if (own) {
      try {
        if (platform === "android") {
          // Play's management page takes the product id (`sku=`); the
          // server's offer names it. No answer → the package-only page.
          let productId: string | undefined;
          const offer = await withBillingTimeout(getGoogleOffer().catch(() => null));
          if (offer && offer.ok) productId = offer.data.product_id;
          await p.manageSubscriptions(productId ? { productId } : undefined);
        } else {
          await p.manageSubscriptions();
        }
        return { ok: true };
      } catch {
        /* fall through to the fixed URL */
      }
    }
  }
  return openExternal(action === "apple_store" ? APPLE_MANAGE_URL : GOOGLE_MANAGE_URL);
}

// ─── The onboarding step's show/hide rule ─────────────────────────────

export type SubscriptionStateName =
  | "none"
  | "trial"
  | "active"
  | "comped"
  | "grace"
  | "locked";

/** Whether the "Start your free month" step is owed. Shown once per account,
 *  only for `none` / `grace`, only where this surface can sell (or on the
 *  web), never on the kiosk, never for `comped` / `trial` / `active`. */
export function subscriptionStepOwed(input: {
  state: SubscriptionStateName | null | undefined;
  platform: BillingPlatform;
  supported: boolean;
  done: boolean;
}): boolean {
  if (input.done) return false;
  if (input.platform === "kiosk") return false;
  if (input.state !== "none" && input.state !== "grace") return false;
  return input.supported || input.platform === "web";
}

/** "Not now" is offered only while enforcement is off. Cycle 468 made
 *  enforcement real: `enforced` is the server's per-account answer (false
 *  for an exempt account — an admin, the demo account, a comped account, a
 *  hero login with no CGM of its own — whatever the switch says), so an
 *  exempt account always keeps "Not now". */
export function notNowAllowed(enforced: boolean): boolean {
  return !enforced;
}

// ─── Plain-words status (Settings + the kiosk card) ───────────────────

/** "Nov 8" in the viewer's own time zone; null for a missing / bad date. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface StatusInput {
  state: SubscriptionStateName;
  trial_ends_at: string | null;
  renews_at: string | null;
  grace_ends_at: string | null;
}

/** The one-line state, in plain words (no em dashes). */
export function subscriptionStateLine(s: StatusInput): string {
  switch (s.state) {
    case "trial": {
      const d = shortDate(s.trial_ends_at);
      return d ? `Free trial, ends ${d}` : "Free trial";
    }
    case "active": {
      const d = shortDate(s.renews_at);
      return d ? `Active, renews ${d}` : "Active";
    }
    case "comped":
      return "Free for life";
    case "grace": {
      const d = shortDate(s.grace_ends_at);
      return d ? `Ended, service continues until ${d}` : "Ended, service continues for now";
    }
    case "locked":
      return "Ended";
    case "none":
    default:
      return "No subscription";
  }
}

/** Where the account is billed, or null when nothing bills it. */
export function subscriptionSourceLine(source: string | null): string | null {
  switch (source) {
    case "apple":
      return "Billed through the App Store";
    case "google":
      return "Billed through Google Play";
    case "stripe":
      return "Billed through thesugaralarm.com";
    case "comp":
      return "Complimentary";
    default:
      return null;
  }
}

/** The line the kiosk card carries, verbatim. */
export const KIOSK_SUBSCRIPTION_LINE =
  "Subscriptions are managed in The Sugar Alarm app on your phone.";
